// src/shell/Shell.tsx
import { useState, useCallback, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { CommandPalette } from './CommandPalette'
import './shell.css'

export function Shell() {
  const [cmdOpen, setCmdOpen] = useState(false)
  // Tiroir de la barre latérale sous 760 px (shell.css) ; sans effet au-dessus.
  const [navOpen, setNavOpen] = useState(false)
  const closeNav = useCallback(() => setNavOpen(false), [])
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  // Échap rend le focus au bouton « Menu », une fois <main> sorti de l'état inerte.
  const focusMenuOnClose = useRef(false)
  const { pathname } = useLocation()

  // Toute navigation referme le tiroir (lien, palette de commandes, retour arrière).
  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  // Au-dessus de 760 px, le tiroir n'existe pas : on le referme pour ne jamais laisser <main> inerte.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const narrow = window.matchMedia('(max-width: 760px)')
    const onChange = (e: { matches: boolean }) => {
      if (!e.matches) setNavOpen(false)
    }
    narrow.addEventListener('change', onChange)
    return () => narrow.removeEventListener('change', onChange)
  }, [])

  // Focus : premier lien du tiroir à l'ouverture, bouton « Menu » après Échap.
  useEffect(() => {
    if (navOpen) {
      document.getElementById('shell-sidebar')?.querySelector('a')?.focus()
    } else if (focusMenuOnClose.current) {
      focusMenuOnClose.current = false
      menuButtonRef.current?.focus()
    }
  }, [navOpen])

  const open = useCallback(() => setCmdOpen(true), [])
  const close = useCallback(() => setCmdOpen(false), [])

  // Raccourci Cmd+K (Mac) ou Ctrl+K (Windows/Linux)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdOpen((v) => !v)
      }
      if (e.key === 'Escape' && cmdOpen) {
        close()
      }
      if (e.key === 'Escape' && navOpen) {
        focusMenuOnClose.current = true
        setNavOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [cmdOpen, close, navOpen])

  return (
    <div
      className="shell"
      style={{
        display: 'flex',
        overflow: 'hidden',
        background: 'var(--bg)',
      }}
    >
      <Sidebar onOpenCommandPalette={open} open={navOpen} onNavigate={closeNav} />
      {navOpen && <div className="shell-backdrop" aria-hidden="true" onClick={closeNav} />}
      <main
        inert={navOpen}
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="shell-topbar">
          <button
            type="button"
            ref={menuButtonRef}
            className="shell-menu-button"
            aria-expanded={navOpen}
            aria-controls="shell-sidebar"
            onClick={() => setNavOpen((v) => !v)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
            Menu
          </button>
        </div>
        <Outlet />
      </main>
      <CommandPalette open={cmdOpen} onClose={close} />
    </div>
  )
}
